import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ethers } from 'ethers'
import { useAccount, useConnectorClient } from 'wagmi'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Input } from '../components/ui/input'
import { toast } from 'sonner'
import { Shield, CheckCircle, XCircle, Eye, Loader2, ArrowLeft, Mail, Link as LinkIcon, TrendingUp } from 'lucide-react'
import DarkModeToggle from '../components/DarkModeToggle'
import { formatPR } from '../utils/prCurrency'
import { formatNumber } from '../utils/formatNumber'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

export default function AdminPanel() {
  const navigate = useNavigate()
  const { token, address } = useAuth()
  const { isDarkMode } = useTheme()
  const { connector } = useAccount()
  const { data: client } = useConnectorClient()
  const [isVerifying, setIsVerifying] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const [reviews, setReviews] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedReview, setSelectedReview] = useState(null)
  const [showReviewDialog, setShowReviewDialog] = useState(false)
  const [reviewNotes, setReviewNotes] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState(false)
  const [adminPassword, setAdminPassword] = useState('')
  const [allUsers, setAllUsers] = useState([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [activeTab, setActiveTab] = useState('reviews') // 'reviews' or 'users'
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    verifyAdmin()
  }, [])

  // Filter users based on search query
  const filteredUsers = allUsers.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const verifyAdmin = async () => {
    try {
      const response = await axios.get(`${API}/profiles/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      const allowedAdmins = ['publicrespectadmin', 'suryasanand']
      if (!allowedAdmins.includes(response.data.username)) {
        toast.error('Access denied. Admin only.')
        navigate('/home')
        return
      }

      // Show password prompt
      setIsVerifying(false)
      setShowPasswordDialog(true)
    } catch (err) {
      console.error('Admin verification error:', err)
      toast.error('Failed to verify admin access')
      navigate('/home')
      setIsVerifying(false)
    }
  }

  const handlePasswordSubmit = async () => {
    if (!adminPassword) {
      toast.error('Please enter password')
      return
    }

    setIsVerifying(true)
    try {
      const verifyResponse = await axios.post(
        `${API}/admin/verify-password`,
        { password: adminPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (verifyResponse.data.verified) {
        setIsAdmin(true)
        setShowPasswordDialog(false)
        fetchReviews()
        fetchAllUsers()
      } else {
        toast.error('Invalid password')
      }
    } catch (err) {
      console.error('Password verification error:', err)
      toast.error('Invalid password')
    } finally {
      setIsVerifying(false)
    }
  }

  const fetchReviews = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(`${API}/admin/manual-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setReviews(response.data)
    } catch (err) {
      console.error('Fetch reviews error:', err)
      toast.error('Failed to load reviews')
    } finally {
      setIsLoading(false)
    }
  }


  const fetchAllUsers = async () => {
    setIsLoadingUsers(true)
    try {
      const response = await axios.get(`${API}/admin/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setAllUsers(response.data)
    } catch (err) {
      console.error('Fetch users error:', err)
      toast.error('Failed to load users')
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleBanUser = async (userWallet, currentBanStatus) => {
    const action = currentBanStatus ? 'unban' : 'ban'
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return

    try {
      await axios.post(
        `${API}/admin/ban-user`,
        {
          user_wallet: userWallet,
          is_banned: !currentBanStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success(`User ${action}ned successfully!`)
      fetchAllUsers()
    } catch (err) {
      console.error('Ban user error:', err)
      toast.error(err.response?.data?.detail || `Failed to ${action} user`)
    }
  }


  const handleReviewAction = async (action) => {
    if (!selectedReview) return

    setIsProcessing(true)
    try {
      await axios.post(
        `${API}/admin/review-decision`,
        {
          review_id: selectedReview.id,
          decision: action,
          notes: reviewNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      toast.success(`Review ${action === 'approve' ? 'approved' : 'rejected'} successfully!`)
      setShowReviewDialog(false)
      setSelectedReview(null)
      setReviewNotes('')
      fetchReviews()
    } catch (err) {
      console.error('Review action error:', err)
      toast.error(err.response?.data?.detail || 'Failed to process review')
    } finally {
      setIsProcessing(false)
    }
  }

  const openReviewDialog = (review) => {
    setSelectedReview(review)
    setReviewNotes('')
    setShowReviewDialog(true)
  }

  if (isVerifying && !showPasswordDialog) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'}`}>
        <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
        <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Verifying admin access...</p>
      </div>
    )
  }

  const pendingReviews = reviews.filter(r => r.status === 'pending')
  const approvedReviews = reviews.filter(r => r.status === 'approved')
  const rejectedReviews = reviews.filter(r => r.status === 'rejected')

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'} py-8 px-4`}>
      {isAdmin ? (
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <Button onClick={() => navigate('/home')} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <DarkModeToggle />
          </div>

          <Card className={`mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
          <CardHeader>
            <CardTitle className={`text-3xl font-bold flex items-center gap-3 ${isDarkMode ? 'text-gray-100' : ''}`}>
              <Shield className="w-8 h-8 text-indigo-600" />
              Admin Panel - Manual Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>Pending</p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-yellow-100' : 'text-yellow-900'}`}>{pendingReviews.length}</p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}>Approved</p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-green-100' : 'text-green-900'}`}>{approvedReviews.length}</p>
              </div>
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-red-200' : 'text-red-800'}`}>Rejected</p>
                <p className={`text-3xl font-bold ${isDarkMode ? 'text-red-100' : 'text-red-900'}`}>{rejectedReviews.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 ${activeTab === 'reviews' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-500 hover:bg-gray-600'}`}
          >
            Verification Reviews
          </Button>
          <Button
            onClick={() => setActiveTab('users')}
            className={`flex-1 ${activeTab === 'users' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-500 hover:bg-gray-600'}`}
          >
            All Users ({allUsers.length})
          </Button>
        </div>

        {activeTab === 'reviews' && (
          <>
            <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Pending Reviews</h2>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : pendingReviews.length === 0 ? (
          <Card className={`mb-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <CardContent className="py-12 text-center">
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No pending reviews</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 mb-8">
            {pendingReviews.map(review => (
              <Card key={review.id} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={review.profile_image} alt={review.name} />
                      <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
                        {review.name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{review.name}</h3>
                        <Badge className="bg-yellow-500">Pending</Badge>
                        {review.is_claimed_profile && (
                          <Badge className="bg-orange-600 text-white font-bold animate-pulse">CLAIMED PROFILE</Badge>
                        )}
                      </div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{review.username}</p>
                      
                      {review.is_claimed_profile && review.scouted_by && (
                        <div className={`mt-2 p-2 rounded ${isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50'}`}>
                          <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                            <strong>Scouted by:</strong> {review.scouted_by.slice(0, 10)}...
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                            10% bounty will be paid to scout upon approval
                          </p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <Mail className="w-4 h-4 inline mr-1" />Contact:
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{review.contact_email}</p>
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <TrendingUp className="w-4 h-4 inline mr-1" />Stats:
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatNumber(review.respect_points)} Respect | {formatPR(review.respect_points)} PR
                          </p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Withdrawable: {review.withdrawable_balance?.toFixed(4)} PR
                          </p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <p className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <LinkIcon className="w-4 h-4 inline mr-1" />Confirmed Links ({review.confirmed_links?.length}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {review.confirmed_links?.map(linkType => (
                            <a
                              key={linkType}
                              href={review.social_links?.[linkType]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-sm px-3 py-1 rounded-full ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'} hover:underline`}
                            >
                              {linkType}
                            </a>
                          ))}
                        </div>
                      </div>

                      <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                          <strong>Profile Link:</strong> {window.location.origin}{review.profile_link}
                        </p>
                      </div>

                      <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        Submitted: {new Date(review.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => window.open(`${window.location.origin}${review.profile_link}`, '_blank')}
                      variant="outline"
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />View Profile
                    </Button>
                    <Button
                      onClick={() => openReviewDialog(review)}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Shield className="w-4 h-4 mr-2" />Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {(approvedReviews.length > 0 || rejectedReviews.length > 0) && (
          <>
            <h2 className={`text-2xl font-bold mb-4 mt-8 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>Reviewed</h2>
            <div className="grid grid-cols-1 gap-4">
              {[...approvedReviews, ...rejectedReviews].map(review => (
                <Card key={review.id} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={review.profile_image} alt={review.name} />
                          <AvatarFallback>{review.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{review.name}</h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{review.username}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={review.status === 'approved' ? 'bg-green-500' : 'bg-red-500'}>
                          {review.status === 'approved' ? 'Approved' : 'Rejected'}
                        </Badge>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {new Date(review.reviewed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {review.review_notes && (
                      <p className={`text-sm mt-2 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Note: {review.review_notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
        </>
        )}

        {/* All Users Tab */}
        {activeTab === 'users' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>All Users</h2>
              <div className="w-64">
                <Input
                  type="text"
                  placeholder="Search by name or username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}
                />
              </div>
            </div>
            {isLoadingUsers ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <Card className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
                <CardContent className="py-12 text-center">
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {searchQuery ? 'No users found matching your search' : 'No users found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredUsers.map(user => (
                  <Card key={user.wallet_address} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={user.profile_image} alt={user.name} />
                            <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{user.name}</h4>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{user.username}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <Badge className={user.is_banned ? 'bg-red-500' : 'bg-green-500'}>
                              {user.is_banned ? 'Banned' : 'Active'}
                            </Badge>
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {formatNumber(user.respect_points)} Respect
                            </p>
                          </div>
                          <Button
                            onClick={() => handleBanUser(user.wallet_address, user.is_banned)}
                            className={user.is_banned ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                            size="sm"
                          >
                            {user.is_banned ? 'Unban' : 'Ban'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
        </div>
      ) : null}

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className={`max-w-md ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-gray-100' : ''}>Admin Password Required</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Enter the admin password to access the panel
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-password" className={isDarkMode ? 'text-gray-200' : ''}>Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter admin password"
                className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setShowPasswordDialog(false)
                  navigate('/home')
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordSubmit}
                disabled={isVerifying}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              >
                {isVerifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Verify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className={`max-w-2xl ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDarkMode ? 'text-gray-100' : ''}>Review Submission</DialogTitle>
            <DialogDescription className={isDarkMode ? 'text-gray-400' : ''}>
              Approve or reject this verification request
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={selectedReview.profile_image} alt={selectedReview.name} />
                  <AvatarFallback>{selectedReview.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedReview.name}</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>@{selectedReview.username}</p>
                </div>
              </div>

              <div>
                <Label className={isDarkMode ? 'text-gray-200' : ''}>Review Notes (Optional)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes about this review decision..."
                  className={`mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : ''}`}
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleReviewAction('reject')}
                  disabled={isProcessing}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                  Reject
                </Button>
                <Button
                  onClick={() => handleReviewAction('approve')}
                  disabled={isProcessing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
